import type { GlobalIllumination, PageState, ServerMessage } from '@roll1/shared';
import type { CanvasContextValue } from '../contexts/CanvasContext';
import type { TokenContextValue } from '../contexts/TokenContext';
import type { UsePagesReturn } from '../hooks/usePages';
import { applyPageEntityMessage } from './page-entity-sync';

/**
 * How a page-content echo (tokens, drawings, fog, notes, pins, map objects,
 * walls, location links, map, page settings) reaches a client — in ONE place
 * for every surface that shows a page on a canvas: the table router
 * (useMultiplayerSync) and the GM Studio router (StudioMapPanes).
 *
 * A page's content lives in exactly one of two copies at a time:
 *  - the live canvas hooks of the canvas that shows it (the table's viewed
 *    page, a Studio map pane's page) — `applyEchoToCanvas`;
 *  - otherwise its page-store copy — `applyEchoToStoredPage`.
 * The canvas copy goes back to the store when that canvas stops showing the
 * page. The two copies share nested objects (strokes, auras), so an echo must
 * never be applied to both: several updates are relative (drawing:moved) or
 * push into shared arrays (aura).
 *
 * A few store fields are kept for every page whoever shows it (walls, map
 * objects, annotations, triggers, location links, map url, page settings) —
 * `applySharedPageEcho`, exactly once per echo, before the canvas reads them.
 *
 * Studio keeps a second Studio-only store field fresh: text objects
 * (`applyTextEchoToStoredPage`) — a Studio pane is remounted on tab switch /
 * full / return and reloads its text from the store.
 */

export type CanvasEchoSink =
  Pick<CanvasContextValue, 'engine' | 'drawing' | 'fog' | 'dynamicFog' | 'wallsHook' | 'locationLinks' | 'mapObjects' | 'globalIlluminationRef'>
  & Pick<TokenContextValue, 'tokens' | 'notes' | 'pins'>;

type PageStoreApi = Pick<
  UsePagesReturn,
  'getPageData' | 'handleMapChanged' | 'handleLocationLinkCreated' | 'handleLocationLinkUpdated' | 'handleLocationLinkDeleted'
>;

/**
 * A ping / ruler is drawn on one map (no pageId — an older client or the AI:
 * the players' map). A canvas shows it only while it shows that map; the
 * server sends players only those of their own map.
 */
export function isOnViewedMap(pageId: string | undefined, viewingPageId: string, activePageId: string): boolean {
  return (pageId ?? activePageId) === viewingPageId;
}

/** The page a page-content echo belongs to; null for any other message. */
export function canvasEchoPageId(msg: ServerMessage): string | null {
  switch (msg.type) {
    case 'note:created': return msg.note.pageId;
    case 'pin:created': return msg.pin.pageId;
    case 'token:moved':
    case 'token:created':
    case 'token:updated':
    case 'token:deleted':
    case 'token:aura-added':
    case 'token:aura-updated':
    case 'token:aura-removed':
    case 'drawing:added':
    case 'drawing:erased':
    case 'drawing:moved':
    case 'drawing:deleted':
    case 'drawing:cleared':
    case 'fog:revealed':
    case 'fog:revealed-batch':
    case 'fog:reset':
    case 'fog:toggled':
    case 'fog:closed':
    case 'fog:closed-batch':
    case 'fog:covered-all':
    case 'fog:cleared-all':
    case 'fog:mode-changed':
    case 'fog:explored-updated':
    case 'note:updated':
    case 'note:deleted':
    case 'note:moved':
    case 'pin:updated':
    case 'pin:deleted':
    case 'pin:moved':
    case 'map-object:created':
    case 'map-object:updated':
    case 'map-object:deleted':
    case 'map-object:reordered':
    case 'wall:created':
    case 'wall:updated':
    case 'wall:deleted':
    case 'walls:created':
    case 'wall:auto-synced':
    case 'door:toggled':
    case 'location:link-created':
    case 'location:link-updated':
    case 'location:link-deleted':
    case 'map:changed':
    case 'page:settings-updated':
      return msg.pageId;
    default:
      return null;
  }
}

/** Store fields kept for every page, whoever shows it. Apply once per echo. */
export function applySharedPageEcho(pages: PageStoreApi, msg: ServerMessage): void {
  if ('pageId' in msg && typeof msg.pageId === 'string') {
    const page = pages.getPageData(msg.pageId);
    if (page) applyPageEntityMessage(page, msg);
  }
  switch (msg.type) {
    case 'map:changed':
      pages.handleMapChanged(msg.pageId, msg.url);
      break;
    case 'page:settings-updated': {
      const pd = pages.getPageData(msg.pageId);
      if (pd) {
        if (msg.changes.gridSize !== undefined) pd.gridSize = msg.changes.gridSize;
        if (msg.changes.mapScale !== undefined) (pd as unknown as Record<string, unknown>).mapScale = msg.changes.mapScale;
        if (msg.changes.mapOffsetX !== undefined) pd.mapOffsetX = msg.changes.mapOffsetX;
        if (msg.changes.mapOffsetY !== undefined) pd.mapOffsetY = msg.changes.mapOffsetY;
        if (msg.changes.bgColor !== undefined) (pd as unknown as Record<string, unknown>).bgColor = msg.changes.bgColor;
        if (msg.changes.globalIllumination !== undefined) {
          pd.globalIllumination = { ...(pd.globalIllumination ?? { level: 'bright' as const }), ...msg.changes.globalIllumination } as GlobalIllumination;
        }
      }
      break;
    }
    case 'location:link-created': pages.handleLocationLinkCreated(msg.pageId, msg.link); break;
    case 'location:link-updated': pages.handleLocationLinkUpdated(msg.pageId, msg.link); break;
    case 'location:link-deleted': pages.handleLocationLinkDeleted(msg.pageId, msg.linkId); break;
    default: break;
  }
}

/**
 * The echo → the live hooks of the canvas showing its page. `page` is that
 * page's store copy (after applySharedPageEcho): location links and the
 * global illumination are read back from it.
 */
export function applyEchoToCanvas(sink: CanvasEchoSink, msg: ServerMessage, page: PageState | null): void {
  const { tokens, notes, pins, drawing, fog, dynamicFog, engine, wallsHook, mapObjects, locationLinks, globalIlluminationRef } = sink;
  switch (msg.type) {
    case 'token:moved':
      tokens.updateToken(msg.tokenId, { x: msg.x, y: msg.y });
      break;
    case 'token:created':
      {
        const have = tokens.tokens.find((t) => t.id === msg.token.id);
        if (have === undefined) tokens.addTokenDirect({ ...msg.token, imageEl: null });
        // The server may have dropped the sheet binding the sender claimed (a sheet has ONE token): the server's word stands,
        // else the sender keeps a «bound» ghost that the party count of a map show counts and the transfer does not carry.
        else if (have.characterSheetId !== msg.token.characterSheetId) tokens.updateToken(msg.token.id, { characterSheetId: msg.token.characterSheetId });
      }
      engine.markDirty('token');
      break;
    case 'token:updated':
      tokens.updateToken(msg.tokenId, msg.changes);
      break;
    case 'token:deleted':
      tokens.removeToken(msg.tokenId);
      break;
    case 'token:aura-added': {
      const tkn = tokens.tokens.find((tk) => tk.id === msg.tokenId);
      if (tkn) tokens.updateToken(msg.tokenId, { auras: [...tkn.auras, msg.aura] });
      break;
    }
    case 'token:aura-updated': {
      const tkn = tokens.tokens.find((tk) => tk.id === msg.tokenId);
      if (tkn) tokens.updateToken(msg.tokenId, { auras: tkn.auras.map((a) => a.id === msg.aura.id ? msg.aura : a) });
      break;
    }
    case 'token:aura-removed': {
      const tkn = tokens.tokens.find((tk) => tk.id === msg.tokenId);
      if (tkn) tokens.updateToken(msg.tokenId, { auras: tkn.auras.filter((a) => a.id !== msg.auraId) });
      break;
    }
    case 'drawing:added':
      drawing.strokes.current.push(msg.stroke);
      drawing.invalidateCache();
      engine.markDirty('drawing');
      break;
    case 'drawing:erased':
      drawing.strokes.current = msg.strokeId
        ? drawing.strokes.current.filter((stroke) => stroke.id !== msg.strokeId)
        : drawing.strokes.current.filter((_, index) => index !== msg.strokeIndex);
      drawing.invalidateCache();
      engine.markDirty('drawing');
      break;
    case 'drawing:moved': {
      const moveIdSet = new Set(msg.strokeIds as string[]);
      for (const stroke of drawing.strokes.current) {
        if (!moveIdSet.has(stroke.id)) continue;
        if (stroke.type === 'path' && stroke.points) {
          for (const p of stroke.points) { p.x += msg.dx; p.y += msg.dy; }
        }
        if (stroke.x1 !== undefined) stroke.x1 += msg.dx;
        if (stroke.y1 !== undefined) stroke.y1 += msg.dy;
        if (stroke.x2 !== undefined) stroke.x2 += msg.dx;
        if (stroke.y2 !== undefined) stroke.y2 += msg.dy;
      }
      drawing.invalidateCache();
      engine.markDirty('drawing');
      break;
    }
    case 'drawing:deleted': {
      const deleteIdSet = new Set(msg.strokeIds as string[]);
      drawing.strokes.current = drawing.strokes.current.filter((s) => !deleteIdSet.has(s.id));
      drawing.invalidateCache();
      engine.markDirty('drawing');
      break;
    }
    case 'drawing:cleared':
      drawing.strokes.current = [];
      drawing.invalidateCache();
      engine.markDirty('drawing');
      break;
    case 'fog:revealed': fog.revealed.current.push(msg.reveal); fog.invalidateCache(); engine.markDirty('fog'); break;
    case 'fog:revealed-batch': fog.revealed.current.push(...msg.reveals); fog.invalidateCache(); engine.markDirty('fog'); break;
    case 'fog:reset': fog.revealed.current = []; fog.closings.current = []; fog.invalidateCache(); dynamicFog.setExplored([]); engine.markDirty('fog'); break;
    case 'fog:toggled': fog.enabled.current = msg.enabled; fog.invalidateCache(); engine.markDirty('fog'); break;
    case 'fog:closed': fog.closings.current.push(msg.closing); fog.invalidateCache(); engine.markDirty('fog'); break;
    case 'fog:closed-batch': fog.closings.current.push(...msg.closings); fog.invalidateCache(); engine.markDirty('fog'); break;
    case 'fog:covered-all': fog.revealed.current = []; fog.closings.current = []; fog.enabled.current = true; fog.invalidateCache(); engine.markDirty('fog'); break;
    // «Убрать всё» стирает и explored (как fog:reset) — иначе у не-инициаторов
    // авто-разведанный dynamic-туман живёт до перезагрузки.
    case 'fog:cleared-all': fog.revealed.current = []; fog.closings.current = []; fog.enabled.current = false; fog.invalidateCache(); dynamicFog.setExplored([]); engine.markDirty('fog'); break;
    case 'fog:mode-changed':
      fog.fogModeRef.current = msg.mode;
      // Dynamic mode requires fog enabled
      if (msg.mode === 'dynamic' && !fog.enabled.current) {
        fog.enabled.current = true;
      }
      fog.invalidateCache();
      if (msg.mode === 'dynamic') {
        dynamicFog.invalidateAll();
      }
      engine.markDirty('fog');
      break;
    case 'fog:explored-updated':
      dynamicFog.appendExplored(msg.explored);
      break;
    case 'note:created': notes.setNotes((prev) => [...prev, msg.note]); engine.markDirty('token'); break;
    case 'note:updated': notes.setNotes((prev) => prev.map((n) => n.id === msg.noteId ? { ...n, ...msg.changes } : n)); engine.markDirty('token'); break;
    case 'note:deleted': notes.setNotes((prev) => prev.filter((n) => n.id !== msg.noteId)); engine.markDirty('token'); break;
    case 'note:moved': notes.setNotes((prev) => prev.map((n) => n.id === msg.noteId ? { ...n, x: msg.x, y: msg.y } : n)); engine.markDirty('token'); break;
    case 'pin:created': pins.setPins((prev) => [...prev, msg.pin]); engine.markDirty('token'); break;
    case 'pin:updated': pins.setPins((prev) => prev.map((p) => p.id === msg.pinId ? { ...p, ...msg.changes } : p)); engine.markDirty('token'); break;
    case 'pin:deleted': pins.setPins((prev) => prev.filter((p) => p.id !== msg.pinId)); engine.markDirty('token'); break;
    case 'pin:moved': pins.setPins((prev) => prev.map((p) => p.id === msg.pinId ? { ...p, x: msg.x, y: msg.y } : p)); engine.markDirty('token'); break;
    case 'map:changed':
      engine.loadMap(msg.url);
      break;
    case 'page:settings-updated':
      if (msg.changes.gridSize !== undefined) engine.gridSizeRef.current = msg.changes.gridSize;
      if (msg.changes.mapScale !== undefined) engine.mapScaleRef.current = msg.changes.mapScale;
      if (msg.changes.mapOffsetX !== undefined || msg.changes.mapOffsetY !== undefined) {
        engine.mapOffsetRef.current = {
          x: msg.changes.mapOffsetX ?? engine.mapOffsetRef.current.x,
          y: msg.changes.mapOffsetY ?? engine.mapOffsetRef.current.y,
        };
      }
      if (msg.changes.bgColor !== undefined) engine.bgColorRef.current = msg.changes.bgColor || null;
      if (msg.changes.globalIllumination !== undefined && page?.globalIllumination) {
        globalIlluminationRef.current = page.globalIllumination;
        dynamicFog.invalidateLighting();
      }
      engine.markAllDirty();
      break;
    case 'location:link-created':
    case 'location:link-updated':
    case 'location:link-deleted':
      if (page) locationLinks.setLinks(page.locationLinks);
      break;
    case 'map-object:created': {
      const objs = mapObjects.objects.current;
      if (!objs.some((o) => o.id === msg.mapObject.id)) {
        mapObjects.setObjects([...objs, msg.mapObject]);
      }
      // Invalidate lighting if the new object is a light source
      if (msg.mapObject.category === 'light') dynamicFog.invalidateLighting();
      break;
    }
    case 'map-object:updated': {
      const oldObj = mapObjects.objects.current.find((o) => o.id === msg.objectId);
      mapObjects.setObjects(
        mapObjects.objects.current.map((o) => o.id === msg.objectId ? { ...o, ...msg.changes } : o),
      );
      // Invalidate lighting if a light-category object was changed (state, light props, category)
      if (oldObj?.category === 'light' || msg.changes.category === 'light') dynamicFog.invalidateLighting();
      break;
    }
    case 'map-object:deleted': {
      const deletedObj = mapObjects.objects.current.find((o) => o.id === msg.objectId);
      mapObjects.setObjects(mapObjects.objects.current.filter((o) => o.id !== msg.objectId));
      // Invalidate lighting if deleted object was a light source
      if (deletedObj?.category === 'light') dynamicFog.invalidateLighting();
      break;
    }
    case 'map-object:reordered': {
      const byId = new Map(mapObjects.objects.current.map((o) => [o.id, o]));
      const reordered = msg.objectIds
        .map((id, i) => {
          const obj = byId.get(id);
          return obj ? { ...obj, sortOrder: i } : null;
        })
        .filter((o): o is NonNullable<typeof o> => o !== null);
      mapObjects.setObjects(reordered);
      break;
    }
    // Wall messages (Dynamic Lighting)
    case 'wall:created':
    case 'wall:updated':
    case 'wall:deleted':
    case 'walls:created':
    case 'wall:auto-synced':
    case 'door:toggled':
      wallsHook.handleMessage(msg);
      dynamicFog.invalidateVisibility();
      dynamicFog.invalidateLighting();
      break;
    default:
      break;
  }
}

/**
 * The echo → the store copy of a page no canvas shows. Walls, map objects,
 * links, map url and settings are already in it (applySharedPageEcho).
 */
export function applyEchoToStoredPage(pd: PageState, msg: ServerMessage): void {
  switch (msg.type) {
    case 'token:moved': { const t = pd.tokens.find((tk) => tk.id === msg.tokenId); if (t) { t.x = msg.x; t.y = msg.y; } break; }
    case 'token:created': { const have = pd.tokens.find((t) => t.id === msg.token.id); if (!have) pd.tokens.push(msg.token); else have.characterSheetId = msg.token.characterSheetId; break; }
    case 'token:updated': { const t = pd.tokens.find((tk) => tk.id === msg.tokenId); if (t) Object.assign(t, msg.changes); break; }
    case 'token:deleted': pd.tokens = pd.tokens.filter((tk) => tk.id !== msg.tokenId); break;
    case 'token:aura-added': { const tk = pd.tokens.find((t) => t.id === msg.tokenId); if (tk) tk.auras.push(msg.aura); break; }
    case 'token:aura-updated': {
      const tk = pd.tokens.find((t) => t.id === msg.tokenId);
      if (tk) { const idx = tk.auras.findIndex((a) => a.id === msg.aura.id); if (idx >= 0) tk.auras[idx] = msg.aura; }
      break;
    }
    case 'token:aura-removed': { const tk = pd.tokens.find((t) => t.id === msg.tokenId); if (tk) tk.auras = tk.auras.filter((a) => a.id !== msg.auraId); break; }
    case 'drawing:added': pd.drawings.push(msg.stroke); break;
    case 'drawing:erased':
      pd.drawings = msg.strokeId
        ? pd.drawings.filter((stroke) => stroke.id !== msg.strokeId)
        : pd.drawings.filter((_, index) => index !== msg.strokeIndex);
      break;
    case 'drawing:moved': {
      const moveIdSet = new Set(msg.strokeIds as string[]);
      for (const d of pd.drawings) {
        if (!moveIdSet.has(d.id)) continue;
        if (d.type === 'path' && d.points) {
          for (const p of d.points) { p.x += msg.dx; p.y += msg.dy; }
        }
        if (d.x1 !== undefined) d.x1 += msg.dx;
        if (d.y1 !== undefined) d.y1 += msg.dy;
        if (d.x2 !== undefined) d.x2 += msg.dx;
        if (d.y2 !== undefined) d.y2 += msg.dy;
      }
      break;
    }
    case 'drawing:deleted': {
      const deleteIdSet = new Set(msg.strokeIds as string[]);
      pd.drawings = pd.drawings.filter((d) => !deleteIdSet.has(d.id));
      break;
    }
    case 'drawing:cleared': pd.drawings = []; break;
    case 'fog:revealed': pd.fog.revealed.push(msg.reveal); break;
    case 'fog:revealed-batch': pd.fog.revealed.push(...msg.reveals); break;
    case 'fog:reset': pd.fog.revealed = []; pd.fog.closings = []; pd.fog.explored = []; break;
    case 'fog:toggled': pd.fog.enabled = msg.enabled; break;
    case 'fog:closed': pd.fog.closings.push(msg.closing); break;
    case 'fog:closed-batch': pd.fog.closings.push(...msg.closings); break;
    case 'fog:covered-all': pd.fog.revealed = []; pd.fog.closings = []; pd.fog.enabled = true; break;
    case 'fog:cleared-all': pd.fog.revealed = []; pd.fog.closings = []; pd.fog.explored = []; pd.fog.enabled = false; break;
    case 'fog:mode-changed': pd.fog.mode = msg.mode; break;
    case 'fog:explored-updated': pd.fog.explored.push(...msg.explored); break;
    case 'note:created': pd.notes = [...(pd.notes ?? []), msg.note]; break;
    case 'note:updated': pd.notes = (pd.notes ?? []).map((n) => n.id === msg.noteId ? { ...n, ...msg.changes } : n); break;
    case 'note:deleted': pd.notes = (pd.notes ?? []).filter((n) => n.id !== msg.noteId); break;
    case 'note:moved': { const n = (pd.notes ?? []).find((note) => note.id === msg.noteId); if (n) { n.x = msg.x; n.y = msg.y; } break; }
    case 'pin:created': pd.pins = [...(pd.pins ?? []), msg.pin]; break;
    case 'pin:updated': pd.pins = (pd.pins ?? []).map((p) => p.id === msg.pinId ? { ...p, ...msg.changes } : p); break;
    case 'pin:deleted': pd.pins = (pd.pins ?? []).filter((p) => p.id !== msg.pinId); break;
    case 'pin:moved': { const pin = (pd.pins ?? []).find((p) => p.id === msg.pinId); if (pin) { pin.x = msg.x; pin.y = msg.y; } break; }
    default: break;
  }
}

/** Studio: text objects of any page stay fresh in the store (see header). */
export function applyTextEchoToStoredPage(pd: PageState, msg: ServerMessage): void {
  switch (msg.type) {
    case 'text:created':
      pd.textObjects = [...(pd.textObjects ?? []).filter((t) => t.id !== msg.textObject.id), msg.textObject];
      break;
    case 'text:updated':
      pd.textObjects = (pd.textObjects ?? []).map((t) => (t.id === msg.textId ? { ...t, ...msg.changes } : t));
      break;
    case 'text:deleted':
      pd.textObjects = (pd.textObjects ?? []).filter((t) => t.id !== msg.textId);
      break;
    case 'text:moved':
      pd.textObjects = (pd.textObjects ?? []).map((t) => (t.id === msg.textId ? { ...t, x: msg.x, y: msg.y } : t));
      break;
    default:
      break;
  }
}
